import type { SectionType } from '../model/pages';

export type SectionSpace = 'tight' | 'normal' | 'loose';

const RANK: Record<SectionSpace, number> = { tight: 0, normal: 1, loose: 2 };

// Retuned tighter than before (Neil: the old normal read as "too much").
const MARGIN: Record<SectionSpace, string> = {
  tight: 'mt-6 sm:mt-8',
  normal: 'mt-10 sm:mt-16',
  loose: 'mt-20 sm:mt-28',
};

// Banded sections pad on BOTH sides; per-side so `space` (top) and `space_below`
// (bottom) are independent. Kept tighter than the non-banded margins.
const BAND_TOP: Record<SectionSpace, string> = { tight: 'pt-6', normal: 'pt-8 sm:pt-12', loose: 'pt-20 sm:pt-28' };
const BAND_BOT: Record<SectionSpace, string> = { tight: 'pb-6', normal: 'pb-8 sm:pb-12', loose: 'pb-20 sm:pb-28' };

// Pairs where `cur` belongs to the block above it and should hug it.
const TIGHT_PAIRS = new Set<string>([
  'hero>cta', 'hero>form', 'hero>benefits', 'hero>logos', 'hero>countdown', 'hero>stats',
  'text>cta', 'guarantee>cta', 'pricing>cta', 'features>cta', 'steps>cta', 'benefits>cta',
  'faq>cta', 'testimonial>cta', 'stats>cta', 'gallery>cta', 'video>cta',
  'cta>form', 'cta>booking', 'countdown>benefits', 'countdown>form',
  'text>hero',
]);

/** Tailwind margin-top between two stacked sections. `curAbove` = this section's
 *  `space`; `prevBelow` = the section above's `space_below`. If either override
 *  is set, the LARGER wins and the auto adjacency base is ignored (an explicit
 *  choice always wins). If neither is set, use the adjacency base. `prev === null`
 *  (first section) has no `prevBelow`; it returns `curAbove`'s margin if set, else
 *  ''. Only ever one margin, so gaps never double. Pure. */
export function sectionGap(prev: SectionType | null, cur: SectionType, curAbove?: SectionSpace, prevBelow?: SectionSpace): string {
  const overrides = [curAbove, prevBelow].filter(Boolean) as SectionSpace[];
  if (overrides.length > 0) {
    const level = overrides.reduce((a, b) => (RANK[b] > RANK[a] ? b : a));
    return MARGIN[level];
  }
  if (prev === null) return '';
  const base: SectionSpace = TIGHT_PAIRS.has(`${prev}>${cur}`) ? 'tight' : 'normal';
  return MARGIN[base];
}

// Modern (roomy) styles: every section is its own band, so the rhythm is set
// here. 72px per side on desktop reads as one clear break between sections
// without the 150px+ "demo template" gaps.
const ROOMY_TOP: Record<SectionSpace, string> = { tight: 'pt-6 sm:pt-8', normal: 'pt-12 sm:pt-[72px]', loose: 'pt-20 sm:pt-28' };
const ROOMY_BOT: Record<SectionSpace, string> = { tight: 'pb-6 sm:pb-8', normal: 'pb-12 sm:pb-[72px]', loose: 'pb-20 sm:pb-28' };

/** Vertical padding for a banded section. `top` from the section's `space`,
 *  `bottom` from its `space_below`; each defaults to the base band padding.
 *  `roomy` = the modern styles' rhythm (classic pages never pass it). Pure. */
export function bandPad(top?: SectionSpace, bottom?: SectionSpace, roomy?: boolean): string {
  if (roomy) return `${ROOMY_TOP[top ?? 'normal']} ${ROOMY_BOT[bottom ?? 'normal']}`;
  const t = top ? BAND_TOP[top] : 'pt-8 sm:pt-12';
  const b = bottom ? BAND_BOT[bottom] : 'pb-8 sm:pb-12';
  return `${t} ${b}`;
}
